(function () {
  const TIER_TAGS = [
    { threshold: 20000, tag: 'gift-200', label: '$200+' },
    { threshold: 15000, tag: 'gift-150', label: '$150+' },
    { threshold: 10000, tag: 'gift-100', label: '$100+' },
  ];

  const SELECTORS = {
    root: '[data-free-gift-root]',
    drawerItems: 'cart-drawer-items',
    cartPageAnchor: '[data-free-gift-cart-anchor]',
    cartFooterAnchor: '#main-cart-footer .cart__blocks',
  };

  const productCache = {
    products: null,
  };

  const LiquidFreeGiftProductSource = {
    list() {
      if (productCache.products) return productCache.products;

      const products = Array.isArray(window.freeGiftProducts) ? window.freeGiftProducts : [];
      productCache.products = products.map((product) => ({
        id: Number(product.id),
        handle: String(product.handle || ''),
        title: String(product.title || ''),
        featured_image: product.featured_image || null,
        first_available_variant_id: product.first_available_variant_id
          ? Number(product.first_available_variant_id)
          : null,
        available: Boolean(product.available),
        tags: Array.isArray(product.tags) ? product.tags.map((tag) => String(tag)) : [],
        price: Number(product.price || 0),
        compare_at_price: product.compare_at_price === null ? null : Number(product.compare_at_price || 0),
      }));

      return productCache.products;
    },
  };

  const FreeGiftDataSources = {
    liquid: LiquidFreeGiftProductSource,
  };

  const state = {
    cart: null,
    pendingCartRequest: null,
    pendingSync: null,
    isUpdatingCart: false,
    lastError: '',
    scrollBound: false,
    observerBound: false,
    observerRunning: false,
    observerTimer: null,
  };

  const productSource = window.FreeGiftProductSource || FreeGiftDataSources.liquid;

  function formatMoney(cents) {
    const currency = window.Shopify && window.Shopify.currency ? window.Shopify.currency.active : 'AUD';
    const locale = document.documentElement.lang || 'en-AU';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
      }).format(cents / 100);
    } catch (error) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  function getCartUrl() {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root + 'cart.js';
    }

    if (window.routes && window.routes.cart_url) {
      return window.routes.cart_url + '.js';
    }

    return '/cart.js';
  }

  function getCartUpdateUrl() {
    if (window.routes && window.routes.cart_update_url) return window.routes.cart_update_url;
    return '/cart/update.js';
  }

  function getCartAddUrl() {
    if (window.routes && window.routes.cart_add_url) return window.routes.cart_add_url;
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root + 'cart/add.js';
    }
    return '/cart/add.js';
  }

  function getCheckoutUrl() {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root + 'checkout';
    }
    return '/checkout';
  }

  function getProducts() {
    return productSource.list();
  }

  function getGiftVariantIds() {
    return new Set(
      getProducts()
        .map((product) => product.first_available_variant_id)
        .filter(Boolean)
    );
  }

  function getGiftProductIds() {
    return new Set(getProducts().map((product) => Number(product.id)).filter(Boolean));
  }

  function getProductByVariantId(variantId) {
    return getProducts().find((product) => product.first_available_variant_id === Number(variantId));
  }

  function hasTag(product, tag) {
    return product.tags.some((productTag) => productTag === tag);
  }

  function itemHasFreeGiftProperty(item) {
    if (!item || !item.properties) return false;
    const props = item.properties;

    if (
      props._free_gift ||
      props['_free_gift'] ||
      props['Free Gift'] ||
      props['free_gift'] ||
      props['_Free Gift']
    ) {
      return true;
    }

    // Catch pairs like Title: Free Gift (visible in cart as "Title: Free Gift")
    return Object.keys(props).some((key) => {
      const k = String(key || '').toLowerCase();
      const v = String(props[key] == null ? '' : props[key]).toLowerCase();
      if (k.includes('free gift') || k === '_free_gift') return true;
      if (v.includes('free gift')) return true;
      return false;
    });
  }

  function isGiftCartItem(item) {
    if (!item) return false;
    if (itemHasFreeGiftProperty(item)) return true;

    const giftVariantIds = getGiftVariantIds();
    const giftProductIds = getGiftProductIds();

    if (giftVariantIds.has(Number(item.variant_id))) return true;
    if (giftProductIds.has(Number(item.product_id))) return true;

    // Product tags on cart line (when available from section / cart.js payload)
    const tags = item.product_tags || item.tags || (item.product && item.product.tags) || [];
    if (Array.isArray(tags)) {
      if (tags.some((tag) => /^(free-gift|gift-100|gift-150|gift-200)$/i.test(String(tag)))) {
        return true;
      }
    }

    return false;
  }

  function stripRowQuantityUi(row) {
    if (!row) return;
    // Remove qty +/- only — keep the same trash button as paid products
    row.querySelectorAll(
      'quantity-input, .quantity-popover-container, .quantity.cart-quantity, .quantity__button, .quantity__input'
    ).forEach((el) => {
      if (el.closest && el.closest('cart-remove-button')) return;
      try {
        el.remove();
      } catch (error) {
        el.style.setProperty('display', 'none', 'important');
      }
    });
  }

  function ensureFreeGiftTag(row) {
    if (!row) return;
    row.querySelectorAll('.product-option, dl, .discounts').forEach((el) => {
      el.style.setProperty('display', 'none', 'important');
    });

    const name = row.querySelector('.cart-item__name');
    if (name) {
      name.classList.add('cart-item__name--free-gift');
      // Prefer plain text (no blue link color)
      if (name.tagName === 'A') {
        name.style.setProperty('color', 'rgb(var(--color-foreground))', 'important');
        name.style.setProperty('text-decoration', 'none', 'important');
      }
    }

    if (row.querySelector('.cart-item__free-gift-tag')) return;
    if (!name || !name.parentNode) return;
    const tag = document.createElement('span');
    tag.className = 'cart-item__free-gift-tag';
    tag.textContent = 'Free gift';
    name.insertAdjacentElement('afterend', tag);
  }

  function insertFreeGiftSectionHeader(container, beforeNode) {
    if (!container || !beforeNode) return;
    if (container.querySelector('.cart-free-gift-section-header')) return;

    const isTable = container.tagName === 'TBODY' || container.tagName === 'TABLE';
    let header;

    if (isTable || beforeNode.tagName === 'TR') {
      header = document.createElement('tr');
      header.className = 'cart-free-gift-section-header';
      const cell = document.createElement('td');
      cell.colSpan = 4;
      cell.innerHTML = `
        <div class="cart-free-gift-section">
          <p class="cart-free-gift-section__title">Your free gift</p>
          <p class="cart-free-gift-section__hint">Included with your order.</p>
        </div>
      `;
      header.appendChild(cell);
    } else {
      header = document.createElement('div');
      header.className = 'cart-free-gift-section-header';
      header.innerHTML = `
        <div class="cart-free-gift-section">
          <p class="cart-free-gift-section__title">Your free gift</p>
          <p class="cart-free-gift-section__hint">Included with your order.</p>
        </div>
      `;
    }

    beforeNode.parentNode.insertBefore(header, beforeNode);
  }

  /**
   * Free gifts: compact section, no qty, plain title, trash beside product.
   */
  function hideFreeGiftCartLines(cart) {
    const giftVariantIds = getGiftVariantIds();
    const giftRows = [];

    const markRow = (row) => {
      if (!row || !row.isConnected) return;
      row.setAttribute('data-free-gift-line', 'true');
      row.classList.add('cart-item--free-gift');
      ensureFreeGiftTag(row);
      stripRowQuantityUi(row);
      if (!giftRows.includes(row)) giftRows.push(row);
    };

    if (cart && Array.isArray(cart.items)) {
      cart.items.forEach((item, index) => {
        if (!isGiftCartItem(item)) return;
        const line = index + 1;
        markRow(document.getElementById(`CartItem-${line}`));
        markRow(document.getElementById(`CartDrawer-Item-${line}`));
      });
    }

    document.querySelectorAll('.cart-item, tr.cart-item').forEach((row) => {
      if (!row || !row.isConnected) return;
      if (row.closest && row.closest(SELECTORS.root)) return;

      let isGift = row.hasAttribute('data-free-gift-line') || row.classList.contains('cart-item--free-gift');

      if (!isGift) {
        const text = String(row.textContent || '')
          .replace(/\s+/g, ' ')
          .toLowerCase();
        if (text.includes('title:') && text.includes('free gift')) isGift = true;
        if (row.querySelector('.cart-item__free-gift-tag')) isGift = true;
      }

      if (!isGift) {
        const qtyInput = row.querySelector('[data-quantity-variant-id]');
        const variantId = qtyInput && Number(qtyInput.getAttribute('data-quantity-variant-id'));
        if (variantId && giftVariantIds.has(variantId)) isGift = true;
      }

      if (!isGift) {
        const remove = row.querySelector('[data-variant-id]');
        if (remove && remove.dataset.variantId && giftVariantIds.has(Number(remove.dataset.variantId))) {
          isGift = true;
        }
      }

      if (isGift) markRow(row);
    });

    // Group free gifts after paid products + add section title.
    // Only move rows when order is wrong to avoid MutationObserver loops.
    if (!giftRows.length) return;

    const byParent = new Map();
    giftRows.forEach((row) => {
      const parent = row.parentNode;
      if (!parent) return;
      if (!byParent.has(parent)) byParent.set(parent, []);
      byParent.get(parent).push(row);
    });

    byParent.forEach((rows, parent) => {
      const children = Array.from(parent.children).filter(
        (child) => !child.classList.contains('cart-free-gift-section-header')
      );
      const trailingGifts = children.slice(-rows.length);
      const alreadyGrouped =
        trailingGifts.length === rows.length && rows.every((row, index) => trailingGifts[index] === row);

      if (!alreadyGrouped) {
        rows.forEach((row) => parent.appendChild(row));
      }
      insertFreeGiftSectionHeader(parent, rows[0]);
    });
  }

  function getGiftItems(cart) {
    return Array.isArray(cart.items) ? cart.items.filter((item) => isGiftCartItem(item)) : [];
  }

  function getQualifyingSubtotal(cart) {
    if (!cart || !Array.isArray(cart.items)) return 0;

    return cart.items.reduce((subtotal, item) => {
      if (isGiftCartItem(item)) return subtotal;
      return subtotal + Number(item.final_line_price || item.line_price || 0);
    }, 0);
  }

  function getUnlockedTier(cart) {
    const subtotal = getQualifyingSubtotal(cart);
    return TIER_TAGS.find((tier) => subtotal >= tier.threshold) || null;
  }

  function getVisibleProducts(cart) {
    const tier = getUnlockedTier(cart);
    if (!tier) return [];

    return getProducts().filter((product) => {
      return product.available && product.first_available_variant_id && hasTag(product, tier.tag);
    });
  }

  function getSelectedGiftVariantId(cart) {
    const tier = getUnlockedTier(cart);
    if (!tier) return null;

    const selectedItem = getGiftItems(cart).find((item) => {
      const product = getProductByVariantId(item.variant_id);
      return product && hasTag(product, tier.tag);
    });

    return selectedItem ? Number(selectedItem.variant_id) : null;
  }

  function getSectionsToRender() {
    const sections = [];
    const cartDrawer = document.querySelector('cart-drawer');
    const cartItems = document.getElementById('main-cart-items');
    const cartFooter = document.getElementById('main-cart-footer');

    if (cartDrawer) {
      // Match cart-drawer-items section refresh target so drawer custom elements stay intact
      sections.push({
        section: 'cart-drawer',
        target: 'cart-drawer .drawer__inner',
        source: '.drawer__inner',
      });
    }

    if (document.getElementById('cart-icon-bubble')) {
      sections.push({
        section: 'cart-icon-bubble',
        target: '#cart-icon-bubble',
        source: '.shopify-section',
      });
    }

    if (cartItems && cartItems.dataset.id) {
      sections.push({
        section: cartItems.dataset.id,
        target: '#main-cart-items .js-contents',
        source: '.js-contents',
      });
    }

    if (cartFooter && cartFooter.dataset.id) {
      sections.push({
        section: cartFooter.dataset.id,
        target: '#main-cart-footer .js-contents, #main-cart-footer .cart__blocks',
        source: '.js-contents, .cart__blocks',
      });
    }

    return sections;
  }

  function isValidCartPayload(cart) {
    return Boolean(cart && Array.isArray(cart.items) && typeof cart.item_count === 'number');
  }

  function cartErrorMessage(payload, fallback) {
    if (!payload || typeof payload !== 'object') return fallback;
    const raw = payload.description || payload.message || payload.errors;
    if (raw == null || raw === '') return fallback;
    if (typeof raw === 'string') return raw;
    try {
      return JSON.stringify(raw);
    } catch (error) {
      return fallback;
    }
  }

  function renderSections(cart) {
    if (!cart || !cart.sections) return;

    getSectionsToRender().forEach((section) => {
      const sectionHtml = cart.sections[section.section];
      if (!sectionHtml) return;

      const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const sourceSelectors = String(section.source || '')
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean);
      const targetSelectors = String(section.target || '')
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean);

      for (let i = 0; i < targetSelectors.length; i += 1) {
        const target = document.querySelector(targetSelectors[i]);
        if (!target) continue;

        let source = null;
        for (let j = 0; j < sourceSelectors.length; j += 1) {
          source = parsed.querySelector(sourceSelectors[j]);
          if (source) break;
        }
        if (!source) continue;

        target.innerHTML = source.innerHTML;
        break;
      }
    });

    document.querySelector('cart-drawer')?.classList.toggle('is-empty', cart.item_count === 0);
    document.querySelector('cart-items')?.classList.toggle('is-empty', cart.item_count === 0);
    document.getElementById('main-cart-footer')?.classList.toggle('is-empty', cart.item_count === 0);

    // Theme cart-items strip qty UI after free-gift section paint
    document.querySelectorAll('cart-items, cart-drawer-items').forEach((el) => {
      if (typeof el.stripFreeGiftQuantityUi === 'function') el.stripFreeGiftQuantityUi();
    });
  }

  function fetchCart(force) {
    if (!force && state.pendingCartRequest) return state.pendingCartRequest;

    const request = fetch(getCartUrl(), {
      headers: {
        Accept: 'application/json',
      },
      cache: 'no-store',
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Could not load cart.');
        }
        return response.json();
      })
      .then((cart) => {
        if (!isValidCartPayload(cart)) {
          throw new Error('Could not load cart.');
        }
        state.cart = cart;
        return cart;
      })
      .finally(() => {
        if (state.pendingCartRequest === request) {
          state.pendingCartRequest = null;
        }
      });

    state.pendingCartRequest = request;
    return request;
  }

  function applyCartResult(cart) {
    if (!isValidCartPayload(cart)) {
      throw new Error(cartErrorMessage(cart, 'Could not update cart.'));
    }

    state.cart = cart;
    renderSections(cart);
    queueRender();

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'free-gift',
        cartData: cart,
      });
    }

    return cart;
  }

  function updateCart(updates) {
    const sections = getSectionsToRender();
    const body = {
      updates,
      sections: sections.map((section) => section.section),
      sections_url: window.location.pathname,
    };

    state.isUpdatingCart = true;
    // Invalidate any in-flight cart.js so post-mutation reads are fresh
    state.pendingCartRequest = null;

    return fetch(getCartUpdateUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || !isValidCartPayload(payload)) {
          throw new Error(cartErrorMessage(payload, 'Could not update cart.'));
        }
        return applyCartResult(payload);
      })
      .finally(() => {
        state.isUpdatingCart = false;
      });
  }

  function addGiftItem(variantId) {
    const sections = getSectionsToRender();
    const body = {
      items: [
        {
          id: Number(variantId),
          quantity: 1,
          properties: {
            _free_gift: 'true',
            'Free Gift': 'Yes',
          },
        },
      ],
      sections: sections.map((section) => section.section),
      sections_url: window.location.pathname,
    };

    state.isUpdatingCart = true;
    state.pendingCartRequest = null;

    return fetch(getCartAddUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(cartErrorMessage(payload, 'Could not add free gift.'));
        }
        return payload;
      })
      .then((payload) => {
        // /cart/add.js returns the added line(s), not always a full cart — force a fresh cart.js
        return fetchCart(true).then((cart) => {
          if (payload && payload.sections) {
            cart.sections = payload.sections;
          }
          return applyCartResult(cart);
        });
      })
      .finally(() => {
        state.isUpdatingCart = false;
      });
  }

  function buildGiftUpdates(cart, nextVariantId) {
    const updates = {};

    // Always clear every gift line first (only one free gift allowed)
    getGiftItems(cart).forEach((item) => {
      updates[item.key] = 0;
    });

    // When reusing the same variant already in cart without properties, set qty via update.
    // New adds go through addGiftItem so properties attach for checkout.
    if (nextVariantId) {
      const existing = getGiftItems(cart).find(
        (item) => Number(item.variant_id) === Number(nextVariantId)
      );
      if (existing) {
        updates[existing.key] = 1;
      }
    }

    return updates;
  }

  /**
   * Ensure at most one free gift at qty 1. Remove disallowed/extra gifts.
   * Does not auto-add a gift — that happens on Add gift / checkout.
   */
  function enforceUnlockedTier(cart) {
    if (state.isUpdatingCart) return Promise.resolve(cart);
    if (state.pendingSync) return state.pendingSync;

    const tier = getUnlockedTier(cart);
    const allowedVariantIds = new Set(
      getVisibleProducts(cart).map((product) => Number(product.first_available_variant_id))
    );
    const giftItems = getGiftItems(cart);
    const updates = {};
    let keptGift = false;

    giftItems.forEach((item) => {
      const variantId = Number(item.variant_id);
      const isAllowed = tier && allowedVariantIds.has(variantId);

      // Only one gift line is allowed; extras and invalid tiers are removed
      if (!isAllowed || keptGift) {
        updates[item.key] = 0;
        return;
      }

      keptGift = true;

      // Free gift is always quantity 1 — never more
      if (Number(item.quantity) !== 1) {
        updates[item.key] = 1;
      }
    });

    if (!Object.keys(updates).length) return Promise.resolve(cart);

    state.pendingSync = updateCart(updates)
      .catch((error) => {
        console.error(error);
        state.lastError = window.cartStrings ? window.cartStrings.error : 'Could not update cart.';
      })
      .finally(() => {
        state.pendingSync = null;
      });

    return state.pendingSync;
  }

  /**
   * Set the cart to exactly one free gift (or none). Uses cart/add so the gift
   * carries properties into checkout.
   */
  function setSingleGift(variantId) {
    if (!state.cart) return Promise.reject(new Error('Cart not loaded'));
    if (state.isUpdatingCart) {
      return Promise.reject(new Error('Cart is updating. Please try again.'));
    }

    const cart = state.cart;
    const giftItems = getGiftItems(cart);
    const existingMatch = giftItems.find(
      (item) => Number(item.variant_id) === Number(variantId) && Number(item.quantity) === 1
    );

    // Already the only gift at qty 1 with free-gift properties
    if (
      variantId &&
      existingMatch &&
      giftItems.length === 1 &&
      itemHasFreeGiftProperty(existingMatch)
    ) {
      return Promise.resolve(cart);
    }

    // Validate selection is allowed for the unlocked tier before mutating cart
    if (variantId) {
      const allowedVariantIds = new Set(
        getVisibleProducts(cart).map((product) => Number(product.first_available_variant_id))
      );
      if (!allowedVariantIds.has(Number(variantId))) {
        return Promise.reject(new Error('That free gift is not available for your cart total.'));
      }
    }

    const clearUpdates = {};
    giftItems.forEach((item) => {
      clearUpdates[item.key] = 0;
    });

    const clearPromise = Object.keys(clearUpdates).length
      ? updateCart(clearUpdates)
      : Promise.resolve(cart);

    return clearPromise.then((nextCart) => {
      if (!variantId) return nextCart;
      // Do not call enforceUnlockedTier immediately after add — it can race and strip the new gift.
      // enforceUnlockedTier already runs on cart events / init for cleanup.
      return addGiftItem(variantId);
    });
  }

  function getStyleSheetText() {
    return `
      /* Remove Gift Box app UI everywhere — custom free-gift owns the cart gift UX */
      [id*="giftbox" i],
      [id*="gift-box" i],
      [id*="gfb-" i],
      [class*="giftbox" i],
      [class*="gift-box" i],
      [class*="GiftBox"],
      [class*="gfb-"],
      [class*="gfb_"],
      [data-giftbox],
      [data-gift-box],
      gift-box,
      giftbox-widget,
      #gfb-root,
      .gfb-widget,
      .gfb-cart,
      .gfb-offer {
        display: none !important;
        visibility: hidden !important;
        pointer-events: none !important;
        max-height: 0 !important;
        overflow: hidden !important;
        margin: 0 !important;
        padding: 0 !important;
        border: 0 !important;
      }

      /* Small “Add Gift” link under cart product list (left-aligned) */
      .free-gift-cart-anchor {
        margin: 0.8rem 0 1.2rem;
        text-align: left;
        width: 100%;
      }

      .free-gift {
        box-sizing: border-box;
        flex-shrink: 0;
        margin: 0.4rem 0 0.8rem;
        padding: 0.2rem 0;
        text-align: left;
        width: 100%;
      }

      .free-gift[hidden] {
        display: none !important;
      }

      .free-gift__bar {
        align-items: baseline;
        display: flex;
        flex-wrap: wrap;
        gap: 0.4rem 1rem;
        justify-content: flex-start;
      }

      .free-gift__link {
        background: none;
        border: 0;
        color: rgb(var(--color-link, var(--color-foreground)));
        cursor: pointer;
        font: inherit;
        font-size: 1.3rem;
        font-weight: 500;
        line-height: 1.4;
        padding: 0;
        text-align: left;
        text-decoration: underline;
        text-underline-offset: 0.18em;
      }

      .free-gift__link:hover,
      .free-gift__link:focus-visible {
        opacity: 0.75;
        outline: none;
      }

      /* Free gift delete button: same trash control as paid products */
      .cart-item--free-gift .cart-item__quantity-wrapper,
      [data-free-gift-line='true'] .cart-item__quantity-wrapper {
        align-items: center;
        display: flex !important;
        gap: 0.5rem;
        justify-content: flex-start;
      }

      .cart-item--free-gift cart-remove-button,
      [data-free-gift-line='true'] cart-remove-button {
        display: inline-flex !important;
        margin: 0 !important;
        visibility: visible !important;
      }

      .cart-item--free-gift cart-remove-button .button,
      [data-free-gift-line='true'] cart-remove-button .button {
        display: inline-flex !important;
        min-height: 4.5rem;
        min-width: 4.5rem;
      }

      .free-gift__message {
        color: rgba(var(--color-foreground), 0.72);
        font-size: 1.2rem;
        margin: 0.4rem 0 0;
      }

      cart-drawer .free-gift {
        margin-left: 0;
        margin-right: 0;
      }

      /* Free gift line stays visible, tagged — no qty +/- */
      .cart-item__free-gift-tag {
        background: rgba(var(--color-foreground), 0.08);
        border-radius: 999px;
        color: rgba(var(--color-foreground), 0.85);
        display: inline-block;
        font-size: 1.15rem;
        font-weight: 600;
        line-height: 1.2;
        margin: 0.4rem 0 0.2rem;
        padding: 0.25rem 0.75rem;
      }

      .cart-item--free-gift quantity-input,
      .cart-item--free-gift .quantity,
      .cart-item--free-gift .quantity__button,
      .cart-item--free-gift .quantity__input,
      .cart-item--free-gift .quantity-popover-container,
      [data-free-gift-line='true'] quantity-input,
      [data-free-gift-line='true'] .quantity,
      [data-free-gift-line='true'] .quantity__button,
      [data-free-gift-line='true'] .quantity__input,
      [data-free-gift-line='true'] .quantity-popover-container {
        display: none !important;
      }

      .cart-item__quantity-wrapper--free-gift {
        align-items: center;
        display: flex;
        justify-content: flex-end;
        min-height: 4.5rem;
      }

      /* Do not mount free gift in right-side cart footer blocks */
      #main-cart-footer .cart__blocks > .free-gift {
        display: none !important;
      }

      /* Gift selection popup/modal */
      .free-gift-modal {
        inset: 0;
        position: fixed;
        z-index: 10050;
      }

      .free-gift-modal[hidden] {
        display: none !important;
      }

      .free-gift-modal__backdrop {
        background: rgba(var(--color-foreground), 0.45);
        inset: 0;
        position: absolute;
      }

      .free-gift-modal__dialog {
        background: rgb(var(--color-background));
        border-radius: 0.8rem;
        box-shadow: 0 1.2rem 3.2rem rgba(var(--color-foreground), 0.18);
        box-sizing: border-box;
        color: rgb(var(--color-foreground));
        display: flex;
        flex-direction: column;
        left: 50%;
        max-height: min(72vh, 52rem);
        max-width: 42rem;
        overflow: hidden;
        padding: 0;
        position: absolute;
        top: 50%;
        transform: translate(-50%, -50%);
        width: calc(100% - 2.4rem);
      }

      .free-gift-modal__header {
        align-items: flex-start;
        border-bottom: 0.1rem solid rgba(var(--color-foreground), 0.1);
        display: flex;
        gap: 1rem;
        justify-content: space-between;
        padding: 1.4rem 1.6rem 1.2rem;
      }

      .free-gift-modal__heading {
        display: grid;
        gap: 0.3rem;
      }

      .free-gift-modal__title {
        font-size: 1.6rem;
        font-weight: 700;
        line-height: 1.3;
        margin: 0;
      }

      .free-gift-modal__tier {
        color: rgba(var(--color-foreground), 0.65);
        font-size: 1.2rem;
      }

      .free-gift-modal__close {
        align-items: center;
        background: transparent;
        border: 0;
        border-radius: 0.4rem;
        color: rgb(var(--color-foreground));
        cursor: pointer;
        display: inline-flex;
        flex-shrink: 0;
        font-size: 2rem;
        height: 3.6rem;
        justify-content: center;
        line-height: 1;
        margin: -0.4rem -0.4rem 0 0;
        padding: 0;
        width: 3.6rem;
      }

      .free-gift-modal__close:hover,
      .free-gift-modal__close:focus-visible {
        background: rgba(var(--color-foreground), 0.06);
        outline: none;
      }

      .free-gift-modal__list {
        list-style: none;
        margin: 0;
        max-height: min(48vh, 34rem);
        overflow-x: hidden;
        overflow-y: auto;
        overscroll-behavior: contain;
        padding: 0.6rem 0;
        -webkit-overflow-scrolling: touch;
      }

      .free-gift-modal__option {
        align-items: center;
        background: transparent;
        border: 0;
        border-bottom: 0.1rem solid rgba(var(--color-foreground), 0.06);
        color: rgb(var(--color-foreground));
        cursor: pointer;
        display: grid !important;
        font: inherit;
        gap: 1rem;
        grid-template-columns: 4.4rem minmax(0, 1fr) auto;
        padding: 1rem 1.6rem;
        text-align: left;
        width: 100%;
      }

      .free-gift-modal__option:last-child {
        border-bottom: 0;
      }

      .free-gift-modal__option:hover,
      .free-gift-modal__option:focus-visible {
        background-color: rgba(var(--color-foreground), 0.06);
        outline: none;
      }

      .free-gift-modal__option.is-selected {
        background-color: rgba(var(--color-foreground), 0.08);
      }

      .free-gift-modal__option.is-loading {
        opacity: 0.6;
        pointer-events: none;
      }

      .free-gift-modal__thumb,
      .free-gift-modal__thumb-placeholder {
        background-color: rgba(var(--color-foreground), 0.04) !important;
        border-radius: 0.4rem !important;
        display: block !important;
        height: 4.4rem !important;
        max-height: 4.4rem !important;
        max-width: 4.4rem !important;
        object-fit: contain !important;
        width: 4.4rem !important;
      }

      .free-gift-modal__thumb-placeholder {
        align-items: center;
        color: rgba(var(--color-foreground), 0.45);
        display: flex !important;
        font-size: 1rem;
        justify-content: center;
      }

      .free-gift-modal__option-title {
        display: -webkit-box;
        font-size: 1.3rem;
        font-weight: 600;
        line-height: 1.3;
        overflow: hidden;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
      }

      .free-gift-modal__badge {
        color: rgba(var(--color-foreground), 0.7);
        font-size: 1.15rem;
        font-weight: 600;
        white-space: nowrap;
      }

      .free-gift-modal__footer {
        border-top: 0.1rem solid rgba(var(--color-foreground), 0.1);
        padding: 1rem 1.6rem 1.4rem;
        text-align: center;
      }

      .free-gift-modal__footer[hidden] {
        display: none !important;
      }

      body.free-gift-modal-open {
        overflow: hidden;
      }

      /* Force cart drawer middle pane to scroll with mouse wheel / touch */
      cart-drawer .drawer__inner {
        display: flex !important;
        flex-direction: column !important;
        height: 100% !important;
        max-height: 100% !important;
        min-height: 0 !important;
        overflow: hidden !important;
      }

      cart-drawer .drawer__header,
      cart-drawer .drawer__footer {
        flex-shrink: 0 !important;
      }

      cart-drawer-items {
        display: block !important;
        flex: 1 1 auto !important;
        min-height: 0 !important;
        overflow-x: hidden !important;
        overflow-y: auto !important;
        -webkit-overflow-scrolling: touch !important;
        overscroll-behavior: contain !important;
        touch-action: pan-y !important;
      }

      cart-drawer-items .cart-drawer__form,
      cart-drawer-items .drawer__contents {
        min-height: min-content;
      }

    `;
  }

  function injectStyles() {
    let style = document.querySelector('style[data-free-gift-style]');
    if (!style) {
      style = document.createElement('style');
      style.dataset.freeGiftStyle = 'true';
      document.head.appendChild(style);
    }
    style.textContent = getStyleSheetText();
  }

  function matchesCompetingGiftNode(node) {
    if (!node || node.nodeType !== 1) return false;
    if (node.matches && node.matches(SELECTORS.root)) return false;
    if (node.closest && node.closest(SELECTORS.root)) return false;
    if (node.id === 'disable-gift-box-app') return false;

    const id = String(node.id || '').toLowerCase();
    const className =
      typeof node.className === 'string' ? node.className.toLowerCase() : String(node.className || '').toLowerCase();
    const tag = String(node.tagName || '').toLowerCase();
    const src = String(node.src || node.href || '').toLowerCase();

    if (tag.includes('gift-box') || tag.includes('giftbox')) return true;
    if (id.includes('giftbox') || id.includes('gift-box') || id.includes('gfb-') || id.includes('gfb_')) return true;
    if (
      className.includes('giftbox') ||
      className.includes('gift-box') ||
      className.includes('gfb-') ||
      className.includes('gfb_')
    ) {
      return true;
    }
    if (src.includes('gift-box') || src.includes('giftbox') || src.includes('/gfb')) return true;
    if (node.hasAttribute('data-giftbox') || node.hasAttribute('data-gift-box')) return true;

    return false;
  }

  function hideCompetingGiftWidgets(root) {
    const scope = root || document;
    if (!scope.querySelectorAll) return;

    // Narrow selectors only — avoid scanning every [id*="gift"] on the full page (perf/crash risk)
    const candidates = scope.querySelectorAll(
      'gift-box, giftbox-widget, [data-giftbox], [data-gift-box], [id*="giftbox" i], [id*="gift-box" i], [id*="gfb-" i], [class*="giftbox" i], [class*="gift-box" i], [class*="gfb-" i], [class*="gfb_" i], script[src*="giftbox" i], script[src*="gift-box" i], script[src*="gfb" i], iframe[src*="giftbox" i], iframe[src*="gift-box" i]'
    );

    candidates.forEach((node) => {
      if (!matchesCompetingGiftNode(node)) return;
      if (node.getAttribute && node.getAttribute('data-free-gift-hidden-competitor') === 'true') return;

      // Fully remove Gift Box app nodes so they cannot re-layout the cart
      try {
        node.remove();
      } catch (error) {
        node.style.setProperty('display', 'none', 'important');
        node.setAttribute('data-free-gift-hidden-competitor', 'true');
      }
    });
  }

  function createRoot() {
    const root = document.createElement('section');
    root.className = 'free-gift';
    root.dataset.freeGiftRoot = 'true';
    root.hidden = true;
    root.setAttribute('aria-label', 'Free gift');
    return root;
  }

  function ensureMount(anchor, placement) {
    if (!anchor) return null;

    if (placement === 'after-items') {
      const form =
        anchor.querySelector('form#CartDrawer-Form') ||
        anchor.querySelector('form.cart-drawer__form') ||
        anchor.querySelector('form');

      if (form) {
        let sibling = form.nextElementSibling;
        while (sibling) {
          if (sibling.matches && sibling.matches(SELECTORS.root)) return sibling;
          sibling = sibling.nextElementSibling;
        }
        const root = createRoot();
        form.insertAdjacentElement('afterend', root);
        return root;
      }

      const existingChild = Array.from(anchor.children).find(
        (child) => child.matches && child.matches(SELECTORS.root)
      );
      if (existingChild) return existingChild;
      const root = createRoot();
      anchor.appendChild(root);
      return root;
    }

    if (placement === 'before') {
      const prev = anchor.previousElementSibling;
      if (prev && prev.matches && prev.matches(SELECTORS.root)) return prev;
      const root = createRoot();
      if (!anchor.parentNode) return null;
      anchor.parentNode.insertBefore(root, anchor);
      return root;
    }

    const existing = anchor.querySelector(SELECTORS.root);
    if (existing) return existing;

    const root = createRoot();
    anchor.prepend(root);
    return root;
  }

  function getMounts() {
    const mounts = [];
    const drawerItems = document.querySelector(SELECTORS.drawerItems);
    const cartPageAnchor = document.querySelector(SELECTORS.cartPageAnchor);
    const footerAnchor = document.querySelector(SELECTORS.cartFooterAnchor);

    // Side cart: after product list (left column scroll area)
    const drawerMount = ensureMount(drawerItems, 'after-items');
    // Main cart page: under product list on the left (not in right-side totals)
    const cartPageMount = ensureMount(cartPageAnchor, 'inside') || ensureMount(footerAnchor, 'inside');

    if (drawerMount) mounts.push(drawerMount);
    if (cartPageMount) mounts.push(cartPageMount);

    return mounts;
  }

  function renderProductThumb(product, className) {
    if (product.featured_image) {
      return `<img class="${className}" src="${escapeHtml(product.featured_image)}" alt="" width="44" height="44" loading="lazy" decoding="async">`;
    }

    return `<span class="${className}-placeholder" aria-hidden="true">Gift</span>`;
  }

  function getActiveProduct(products, selectedVariantId) {
    if (selectedVariantId) {
      const match = products.find((product) => Number(product.first_available_variant_id) === Number(selectedVariantId));
      if (match) return match;
    }

    return products[0];
  }

  function ensureModal() {
    let modal = document.querySelector('[data-free-gift-modal]');
    if (modal) return modal;

    modal = document.createElement('div');
    modal.className = 'free-gift-modal';
    modal.dataset.freeGiftModal = 'true';
    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    modal.innerHTML = `
      <div class="free-gift-modal__backdrop" data-free-gift-close tabindex="-1"></div>
      <div
        class="free-gift-modal__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="FreeGiftModalTitle"
        data-free-gift-dialog
      >
        <div class="free-gift-modal__header">
          <div class="free-gift-modal__heading">
            <h2 class="free-gift-modal__title" id="FreeGiftModalTitle">Choose your free gift</h2>
            <span class="free-gift-modal__tier" data-free-gift-modal-tier></span>
          </div>
          <button type="button" class="free-gift-modal__close" data-free-gift-close aria-label="Close">
            ×
          </button>
        </div>
        <ul class="free-gift-modal__list" role="listbox" data-free-gift-modal-list></ul>
        <div class="free-gift-modal__footer" data-free-gift-modal-footer hidden>
          <button type="button" class="free-gift__link" data-free-gift-remove>Remove gift</button>
        </div>
      </div>
    `;
    document.body.appendChild(modal);
    return modal;
  }

  function closeGiftModal() {
    const modal = document.querySelector('[data-free-gift-modal]');
    if (!modal) return;
    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('free-gift-modal-open');
  }

  function openGiftModal() {
    if (!state.cart) return;

    const tier = getUnlockedTier(state.cart);
    const products = getVisibleProducts(state.cart);
    if (!tier || !products.length) return;

    const selectedVariantId = getSelectedGiftVariantId(state.cart);
    const modal = ensureModal();
    const tierEl = modal.querySelector('[data-free-gift-modal-tier]');
    const listEl = modal.querySelector('[data-free-gift-modal-list]');

    if (tierEl) tierEl.textContent = tier.label;
    if (listEl) {
      listEl.innerHTML = products
        .map((product) => {
          const variantId = Number(product.first_available_variant_id);
          const isSelected = Number(selectedVariantId) === variantId;
          return `
            <li role="option" aria-selected="${isSelected ? 'true' : 'false'}">
              <button
                type="button"
                class="free-gift-modal__option${isSelected ? ' is-selected' : ''}"
                data-free-gift-option
                data-variant-id="${variantId}"
              >
                ${renderProductThumb(product, 'free-gift-modal__thumb')}
                <span class="free-gift-modal__option-title">${escapeHtml(product.title)}</span>
                <span class="free-gift-modal__badge">${isSelected ? 'Selected' : 'Free'}</span>
              </button>
            </li>
          `;
        })
        .join('');
    }

    const footer = modal.querySelector('[data-free-gift-modal-footer]');
    if (footer) footer.hidden = !selectedVariantId;

    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('free-gift-modal-open');

    const firstOption = modal.querySelector('[data-free-gift-option]');
    if (firstOption) firstOption.focus();
  }

  function render(cart) {
    injectStyles();
    hideCompetingGiftWidgets(document.querySelector('cart-drawer') || document);
    hideCompetingGiftWidgets(document.getElementById('main-cart-footer') || document);
    hideFreeGiftCartLines(cart);
    ensureCartScroll();
    ensureModal();

    const mounts = getMounts();
    if (!mounts.length) return;

    const tier = getUnlockedTier(cart);
    const products = getVisibleProducts(cart);
    const selectedVariantId = getSelectedGiftVariantId(cart);

    mounts.forEach((mount) => {
      if (!tier || !products.length) {
        mount.hidden = true;
        mount.innerHTML = '';
        return;
      }

      // Small Add Gift link; when a gift is already in cart, also offer Remove
      const selectedValue = selectedVariantId || Number(products[0].first_available_variant_id);

      mount.hidden = false;
      mount.innerHTML = `
        <div class="free-gift__bar">
          <button type="button" class="free-gift__link" data-free-gift-open>Add Gift</button>
        </div>
        <input type="hidden" name="free_gift_variant_id" value="${selectedValue}" data-free-gift-select>
        ${state.lastError ? `<p class="free-gift__message" role="alert">${escapeHtml(state.lastError)}</p>` : ''}
      `;
    });

    hideFreeGiftCartLines(cart);
  }

  function queueRender() {
    window.requestAnimationFrame(() => {
      if (state.cart) render(state.cart);
    });
  }

  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, (character) => {
      return {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      }[character];
    });
  }

  function setModalLoading(loading) {
    const modal = document.querySelector('[data-free-gift-modal]');
    if (!modal) return;
    modal.querySelectorAll('[data-free-gift-option]').forEach((button) => {
      button.classList.toggle('is-loading', loading);
      button.toggleAttribute('disabled', loading);
    });
  }

  function changeGift(variantId) {
    if (!variantId || !state.cart || state.isUpdatingCart) return Promise.resolve();

    state.lastError = '';
    setModalLoading(true);

    return setSingleGift(variantId)
      .then(() => {
        closeGiftModal();
        queueRender();
      })
      .catch((error) => {
        console.error(error);
        state.lastError =
          (error && error.message) ||
          (window.cartStrings ? window.cartStrings.error : 'Could not update cart.');
        queueRender();
      })
      .finally(() => {
        setModalLoading(false);
      });
  }

  function resolveGiftVariantForCheckout(cart) {
    const selected = getSelectedGiftVariantId(cart);
    if (selected) return selected;

    const select = document.querySelector('[data-free-gift-select]');
    if (select && select.value) return Number(select.value);

    const products = getVisibleProducts(cart);
    if (products.length && products[0].first_available_variant_id) {
      return Number(products[0].first_available_variant_id);
    }

    return null;
  }

  function cartNeedsGiftSync(cart) {
    const tier = getUnlockedTier(cart);
    if (!tier) {
      // Tier not unlocked — strip gifts if any lingered
      return getGiftItems(cart).length > 0;
    }

    const giftItems = getGiftItems(cart);
    if (giftItems.length === 0) return true; // eligible but missing gift — add at checkout
    if (giftItems.length > 1) return true;
    if (giftItems.some((item) => Number(item.quantity) !== 1)) return true;

    const allowedVariantIds = new Set(
      getVisibleProducts(cart).map((product) => Number(product.first_available_variant_id))
    );
    if (!allowedVariantIds.has(Number(giftItems[0].variant_id))) return true;

    return false;
  }

  function syncGiftForCheckout(cart) {
    const tier = getUnlockedTier(cart);

    if (!tier) {
      return setSingleGift(null);
    }

    const giftItems = getGiftItems(cart);
    if (
      giftItems.length === 1 &&
      Number(giftItems[0].quantity) === 1 &&
      getVisibleProducts(cart).some(
        (product) => Number(product.first_available_variant_id) === Number(giftItems[0].variant_id)
      )
    ) {
      return Promise.resolve(cart);
    }

    const variantId = resolveGiftVariantForCheckout(cart);
    if (!variantId) return enforceUnlockedTier(cart);
    return setSingleGift(variantId);
  }

  function isCheckoutControl(element) {
    if (!element || element.nodeType !== 1) return false;
    if (element.matches && element.matches('#CartDrawer-Checkout, #checkout, button[name="checkout"], input[name="checkout"]')) {
      return true;
    }
    return Boolean(element.closest && element.closest('#CartDrawer-Checkout, #checkout, button[name="checkout"], input[name="checkout"]'));
  }

  function runCheckoutGiftGate(event, control) {
    if (control && control.dataset.freeGiftCheckoutReady === 'true') return false;
    if (state.isUpdatingCart) {
      event.preventDefault();
      event.stopPropagation();
      return true;
    }

    const lockControl = (busy) => {
      if (!control) return;
      if (busy) {
        control.setAttribute('aria-busy', 'true');
        control.disabled = true;
      } else {
        control.removeAttribute('aria-busy');
        control.disabled = false;
      }
    };

    const proceed = (cart) => {
      if (!cartNeedsGiftSync(cart)) return false;

      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();

      lockControl(true);

      syncGiftForCheckout(cart)
        .then(() => {
          if (control) control.dataset.freeGiftCheckoutReady = 'true';
          lockControl(false);
          // Navigate so the gift is definitely in the cart before Shopify checkout
          window.location.href = getCheckoutUrl();
        })
        .catch((error) => {
          console.error(error);
          lockControl(false);
          state.lastError = window.cartStrings ? window.cartStrings.error : 'Could not add free gift.';
          queueRender();
        });

      return true;
    };

    if (state.cart) {
      return proceed(state.cart);
    }

    event.preventDefault();
    event.stopPropagation();
    fetchCart()
      .then((cart) => {
        if (!proceed(cart)) {
          window.location.href = getCheckoutUrl();
        }
      })
      .catch(() => {
        window.location.href = getCheckoutUrl();
      });
    return true;
  }

  function handleCheckoutIntent(event) {
    const control = isCheckoutControl(event.target)
      ? event.target.closest('#CartDrawer-Checkout, #checkout, button[name="checkout"], input[name="checkout"]') ||
        event.target
      : null;
    if (!control) return;
    runCheckoutGiftGate(event, control);
  }

  function handleCheckoutFormSubmit(event) {
    const form = event.target;
    if (!form || form.nodeType !== 1) return;

    const isCartForm =
      form.id === 'CartDrawer-Form' ||
      form.id === 'cart' ||
      (typeof form.getAttribute === 'function' && String(form.getAttribute('action') || '').includes('/cart'));
    if (!isCartForm) return;

    const submitter = event.submitter;
    const isCheckoutSubmit =
      (submitter && (submitter.name === 'checkout' || isCheckoutControl(submitter))) ||
      (event.submitter == null && form.querySelector('[name="checkout"]'));

    if (!isCheckoutSubmit) return;
    runCheckoutGiftGate(event, submitter || form.querySelector('[name="checkout"]'));
  }

  function handleGiftClick(event) {
    const closeEl = event.target.closest('[data-free-gift-close]');
    if (closeEl) {
      event.preventDefault();
      closeGiftModal();
      return;
    }

    const openEl = event.target.closest('[data-free-gift-open]');
    if (openEl) {
      event.preventDefault();
      openGiftModal();
      return;
    }

    const removeButton = event.target.closest('[data-free-gift-remove]');
    if (removeButton) {
      if (!state.cart || state.isUpdatingCart) return;
      event.preventDefault();
      state.lastError = '';
      removeButton.toggleAttribute('disabled', true);
      setModalLoading(true);

      setSingleGift(null)
        .then(() => {
          closeGiftModal();
        })
        .catch((error) => {
          console.error(error);
          state.lastError = window.cartStrings ? window.cartStrings.error : 'Could not update cart.';
          queueRender();
        })
        .finally(() => {
          removeButton.toggleAttribute('disabled', false);
          setModalLoading(false);
        });
      return;
    }

    const option = event.target.closest('[data-free-gift-option]');
    if (option) {
      event.preventDefault();
      const variantId = Number(option.dataset.variantId);
      if (!variantId) return;
      changeGift(variantId);
      return;
    }
  }

  function handleGiftKeydown(event) {
    if (event.key === 'Escape') {
      closeGiftModal();
    }
  }

  function ensureCartScroll() {
    const drawerItems = document.querySelector(SELECTORS.drawerItems);
    const drawerInner = document.querySelector('cart-drawer .drawer__inner');

    if (drawerInner) {
      drawerInner.style.setProperty('overflow', 'hidden', 'important');
      drawerInner.style.setProperty('min-height', '0', 'important');
      drawerInner.style.setProperty('display', 'flex', 'important');
      drawerInner.style.setProperty('flex-direction', 'column', 'important');
    }

    if (drawerItems) {
      drawerItems.style.setProperty('overflow-y', 'auto', 'important');
      drawerItems.style.setProperty('overflow-x', 'hidden', 'important');
      drawerItems.style.setProperty('flex', '1 1 auto', 'important');
      drawerItems.style.setProperty('min-height', '0', 'important');
      drawerItems.style.setProperty('-webkit-overflow-scrolling', 'touch');
      drawerItems.style.setProperty('overscroll-behavior', 'contain');
      drawerItems.style.setProperty('touch-action', 'pan-y');
    }

    if (state.scrollBound) return;
    state.scrollBound = true;

    // Forward mouse wheel to the cart items pane when hovering the drawer
    document.addEventListener(
      'wheel',
      (event) => {
        const drawer = event.target.closest && event.target.closest('cart-drawer.active, cart-drawer.animate');
        if (!drawer) return;

        const items = drawer.querySelector(SELECTORS.drawerItems);
        if (!items) return;

        // Let nested scroll areas (e.g. gift modal list) handle their own wheel
        const nestedScroll =
          event.target.closest && event.target.closest('.free-gift-modal__list, .free-gift__options');
        if (nestedScroll && nestedScroll.scrollHeight > nestedScroll.clientHeight) {
          const atTop = nestedScroll.scrollTop <= 0 && event.deltaY < 0;
          const atBottom =
            nestedScroll.scrollTop + nestedScroll.clientHeight >= nestedScroll.scrollHeight - 1 && event.deltaY > 0;
          if (!atTop && !atBottom) return;
        }

        if (items.scrollHeight <= items.clientHeight + 1) return;

        const prev = items.scrollTop;
        items.scrollTop += event.deltaY;

        if (items.scrollTop !== prev) {
          event.preventDefault();
        }
      },
      { passive: false, capture: true }
    );
  }

  function observeDrawerMutations() {
    if (state.observerBound) return;
    state.observerBound = true;

    const observer = new MutationObserver(() => {
      // Debounce + re-entrancy guard: hideFreeGiftCartLines mutates DOM and must not loop forever
      if (state.observerRunning) return;
      if (state.observerTimer) window.clearTimeout(state.observerTimer);
      state.observerTimer = window.setTimeout(() => {
        if (state.observerRunning || state.isUpdatingCart) return;
        state.observerRunning = true;
        try {
          hideCompetingGiftWidgets(document.querySelector('cart-drawer') || document);
          hideFreeGiftCartLines(state.cart);
          ensureCartScroll();
          // Re-mount if cart drawer HTML was replaced by section rendering
          if (state.cart && !document.querySelector(SELECTORS.root)) {
            queueRender();
          }
        } catch (error) {
          console.error(error);
        } finally {
          state.observerRunning = false;
        }
      }, 50);
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  function handleCartEvent(event) {
    // Ignore our own publishes to avoid update/publish infinite loops that freeze the page
    if (event && event.source === 'free-gift') return;

    if (event && event.cartData && Array.isArray(event.cartData.items)) {
      state.cart = event.cartData;
      enforceUnlockedTier(event.cartData).then(queueRender);
      window.setTimeout(queueRender, 0);
      window.setTimeout(() => {
        hideCompetingGiftWidgets(document.querySelector('cart-drawer') || document);
        ensureCartScroll();
      }, 50);
      return;
    }

    fetchCart()
      .then((cart) => enforceUnlockedTier(cart))
      .then(queueRender)
      .catch((error) => {
        console.error(error);
      });
  }

  function init() {
    try {
      injectStyles();
      ensureCartScroll();
      observeDrawerMutations();
      hideCompetingGiftWidgets(document);

      document.addEventListener('click', handleGiftClick);
      document.addEventListener('keydown', handleGiftKeydown);
      // Capture so we can attach the free gift before Shopify checkout navigation
      document.addEventListener('click', handleCheckoutIntent, true);
      document.addEventListener('submit', handleCheckoutFormSubmit, true);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, handleCartEvent);
      }

      // Always try to mount once DOM is ready, even if products load later
      const start = () => {
        try {
          if (!getProducts().length) {
            // Still hide competing widgets and keep scroll working
            hideCompetingGiftWidgets(document);
            ensureCartScroll();
            return;
          }

          fetchCart()
            .then((cart) => enforceUnlockedTier(cart))
            .then(queueRender)
            .catch((error) => {
              console.error(error);
            });
        } catch (error) {
          console.error(error);
        }
      };

      start();
      // Retry shortly after load — cart drawer / apps may inject late
      window.setTimeout(start, 400);
      window.setTimeout(() => {
        try {
          hideCompetingGiftWidgets(document);
          ensureCartScroll();
          queueRender();
        } catch (error) {
          console.error(error);
        }
      }, 1200);
    } catch (error) {
      console.error(error);
    }
  }

  window.FreeGiftDataSources = FreeGiftDataSources;
  window.FreeGiftProductSource = productSource;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
